import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ChevronLeft, ChevronRight, ShieldCheck } from "lucide-react";
import { api, PROPERTY_TYPES, CITIES, priceLabel, formatApiErrorDetail } from "@/lib/loop";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { VerificationBadge } from "@/components/Badges";
import { toast } from "sonner";

const STEPS = ["Type", "Location", "Details", "Price", "Photos", "Documents", "Verification", "Preview", "Submit"];
const DOC_CATS = ["Sale Deed", "RoR / Land Records", "Mutation", "Encumbrance Certificate", "Conversion / Gharabari", "Building Approval", "RERA Information", "Tax Receipts", "NOCs"];
const SAMPLE_IMGS = [
  "https://images.unsplash.com/photo-1741526997954-a27031862fb7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  "https://images.unsplash.com/photo-1626249893783-cc4a9f66880a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
];

export default function ListProperty() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [d, setD] = useState({
    property_type: "plot", listing_type: "buy", city: "Bhubaneswar", locality: "",
    title: "", description: "", area_sqft: "", area_display: "", road_width: "", frontage: "",
    facing: "East", bedrooms: 0, ownership: "Single Owner", price: "", price_display: "",
    images: [SAMPLE_IMGS[0]], docs: {}, rera_status: "not_applicable", new_or_resale: "resale",
  });
  const set = (k, v) => setD({ ...d, [k]: v });
  const today = new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

  const submit = async () => {
    try {
      const documents = DOC_CATS.map((c) => ({ category: c, status: d.docs[c] ? "review_pending" : "not_provided" }));
      const payload = {
        ...d,
        price: d.price ? Number(d.price) : null,
        area_sqft: Number(d.area_sqft) || 0,
        bedrooms: Number(d.bedrooms) || 0,
        image_dates: d.images.map(() => today),
        documents,
      };
      const { data } = await api.post("/properties", payload);
      toast.success(`Submitted! ${data.loop_id} is now Verification Pending.`);
      nav("/owner");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const canNext = () => {
    if (step === 1) return d.locality.trim().length > 0;
    if (step === 2) return d.title.trim().length > 0 && d.area_sqft;
    return true;
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-8 pb-28 md:pb-16">
      <div className="text-[11px] uppercase tracking-[0.2em] text-[#C5A059] font-semibold">List Your Property</div>
      <h1 className="mt-1 text-2xl font-bold text-white">Step {step + 1} of {STEPS.length}: {STEPS[step]}</h1>

      {/* Progress */}
      <div className="mt-4 flex gap-1.5">
        {STEPS.map((s, i) => (
          <div key={s} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-[#C5A059]" : "bg-white/10"}`} />
        ))}
      </div>

      <div className="mt-8 bg-[#0B132B] border border-white/10 rounded-2xl p-6 min-h-[340px]">
        {step === 0 && (
          <div>
            <Label className="text-slate-300">Property type</Label>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
              {PROPERTY_TYPES.map((t) => (
                <button key={t.value} data-testid={`wiz-type-${t.value}`} onClick={() => set("property_type", t.value)}
                  className={`py-3 rounded-xl text-sm font-medium border transition-colors ${d.property_type === t.value ? "bg-[#C5A059] text-[#070C1E] border-[#C5A059]" : "text-slate-300 border-white/15"}`}>
                  {t.label}
                </button>
              ))}
            </div>
            <Label className="text-slate-300 mt-6 block">Listing type</Label>
            <div className="mt-2 flex gap-2">
              {["buy", "rent"].map((l) => (
                <button key={l} data-testid={`wiz-listing-${l}`} onClick={() => set("listing_type", l)}
                  className={`px-6 py-2 rounded-lg text-sm font-medium capitalize border ${d.listing_type === l ? "bg-[#C5A059] text-[#070C1E] border-[#C5A059]" : "text-slate-300 border-white/15"}`}>{l}</button>
              ))}
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">City</Label>
              <Select value={d.city} onValueChange={(v) => set("city", v)}>
                <SelectTrigger data-testid="wiz-city" className="mt-1.5 bg-[#131B35] border-white/10 text-white"><SelectValue /></SelectTrigger>
                <SelectContent>{CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Locality</Label>
              <Input data-testid="wiz-locality" value={d.locality} onChange={(e) => set("locality", e.target.value)}
                placeholder="e.g. Patia" className="mt-1.5 bg-[#131B35] border-white/10 text-white" />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div><Label className="text-slate-300">Title</Label>
              <Input data-testid="wiz-title" value={d.title} onChange={(e) => set("title", e.target.value)}
                placeholder="1 Acre Commercial Plot — Patia" className="mt-1.5 bg-[#131B35] border-white/10 text-white" /></div>
            <div><Label className="text-slate-300">Description</Label>
              <Textarea data-testid="wiz-desc" value={d.description} onChange={(e) => set("description", e.target.value)}
                className="mt-1.5 bg-[#131B35] border-white/10 text-white" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-300">Area (sqft)</Label>
                <Input data-testid="wiz-area" type="number" value={d.area_sqft} onChange={(e) => set("area_sqft", e.target.value)} className="mt-1.5 bg-[#131B35] border-white/10 text-white" /></div>
              <div><Label className="text-slate-300">Area label</Label>
                <Input value={d.area_display} onChange={(e) => set("area_display", e.target.value)} placeholder="1 Acre" className="mt-1.5 bg-[#131B35] border-white/10 text-white" /></div>
              <div><Label className="text-slate-300">Road width</Label>
                <Input value={d.road_width} onChange={(e) => set("road_width", e.target.value)} placeholder="40 ft" className="mt-1.5 bg-[#131B35] border-white/10 text-white" /></div>
              <div><Label className="text-slate-300">Frontage</Label>
                <Input value={d.frontage} onChange={(e) => set("frontage", e.target.value)} placeholder="130 ft" className="mt-1.5 bg-[#131B35] border-white/10 text-white" /></div>
              <div><Label className="text-slate-300">Facing</Label>
                <Select value={d.facing} onValueChange={(v) => set("facing", v)}>
                  <SelectTrigger className="mt-1.5 bg-[#131B35] border-white/10 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent>{["East", "West", "North", "South", "North-East", "South-East"].map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-slate-300">Bedrooms</Label>
                <Input type="number" value={d.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} className="mt-1.5 bg-[#131B35] border-white/10 text-white" /></div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <div><Label className="text-slate-300">Asking price (₹) — leave blank for "Contact for Price"</Label>
              <Input data-testid="wiz-price" type="number" value={d.price} onChange={(e) => set("price", e.target.value)} className="mt-1.5 bg-[#131B35] border-white/10 text-white" /></div>
            <div><Label className="text-slate-300">Ownership</Label>
              <Select value={d.ownership} onValueChange={(v) => set("ownership", v)}>
                <SelectTrigger className="mt-1.5 bg-[#131B35] border-white/10 text-white"><SelectValue /></SelectTrigger>
                <SelectContent>{["Single Owner", "Joint Ownership", "Company Owned"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select></div>
            <p className="text-xs text-slate-500">Figures you provide are treated as owner-supplied until reviewed.</p>
          </div>
        )}

        {step === 4 && (
          <div>
            <Label className="text-slate-300">Select photos (sample library)</Label>
            <div className="mt-3 grid grid-cols-3 gap-3">
              {SAMPLE_IMGS.map((img) => {
                const sel = d.images.includes(img);
                return (
                  <button key={img} data-testid="wiz-img" onClick={() => set("images", sel ? d.images.filter((x) => x !== img) : [...d.images, img])}
                    className={`relative rounded-xl overflow-hidden border-2 ${sel ? "border-[#C5A059]" : "border-transparent"}`}>
                    <img src={img} alt="" className="w-full h-24 object-cover" />
                    {sel && <div className="absolute top-1 right-1 bg-[#C5A059] rounded-full p-0.5"><Check className="w-3 h-3 text-[#070C1E]" /></div>}
                  </button>
                );
              })}
            </div>
            <p className="mt-3 text-xs text-slate-500">Every photo will be date-stamped ({today}) on your Property Passport.</p>
          </div>
        )}

        {step === 5 && (
          <div>
            <Label className="text-slate-300">Which documents can you supply?</Label>
            <div className="mt-3 space-y-1">
              {DOC_CATS.map((c) => (
                <label key={c} className="flex items-center gap-3 py-2 border-b border-white/5 cursor-pointer">
                  <Checkbox data-testid={`wiz-doc-${c}`} checked={!!d.docs[c]} onCheckedChange={() => setD({ ...d, docs: { ...d.docs, [c]: !d.docs[c] } })} />
                  <span className="text-sm text-slate-200">{c}</span>
                </label>
              ))}
            </div>
            <p className="mt-3 text-xs text-slate-500">Uploaded documents are marked "review pending" — never auto-confirmed as clear title.</p>
          </div>
        )}

        {step === 6 && (
          <div className="text-center py-8">
            <ShieldCheck className="w-12 h-12 text-[#C5A059] mx-auto" />
            <h3 className="mt-4 text-lg font-bold text-white">Owner / Agent Verification</h3>
            <p className="mt-2 text-sm text-slate-400 max-w-md mx-auto">Your identity will be verified by LOOP after submission. High-risk claims are never published automatically — your listing enters <span className="text-amber-400">Verification Pending</span>.</p>
          </div>
        )}

        {step === 7 && (
          <div>
            <h3 className="text-lg font-bold text-white mb-4">Preview</h3>
            <div className="rounded-2xl overflow-hidden border border-white/10">
              <img src={d.images[0]} alt="" className="w-full h-44 object-cover" />
              <div className="p-4 bg-[#131B35]">
                <div className="flex items-center justify-between">
                  <h4 className="font-bold text-white">{d.title || "Untitled Property"}</h4>
                  <VerificationBadge status="unverified" />
                </div>
                <p className="text-xs text-slate-400 mt-1">{d.locality}, {d.city}</p>
                <p className="text-lg font-extrabold text-[#C5A059] mt-2">{d.price ? "₹" + Number(d.price).toLocaleString("en-IN") : "Contact for Price"}</p>
                <p className="text-xs text-slate-500 mt-1">{d.area_display || d.area_sqft + " sqft"} · {d.road_width || "—"} road · {Object.values(d.docs).filter(Boolean).length} docs</p>
              </div>
            </div>
          </div>
        )}

        {step === 8 && (
          <div className="text-center py-8">
            <h3 className="text-lg font-bold text-white">Submit for verification</h3>
            <p className="mt-2 text-sm text-slate-400 max-w-md mx-auto">Your listing will be reviewed by the LOOP team before it goes live. You'll get a unique Property Passport ID.</p>
            <Button data-testid="wiz-submit" onClick={submit} className="mt-6 bg-[#C5A059] hover:bg-[#b18f47] text-[#070C1E] font-semibold px-10">Submit for Verification</Button>
          </div>
        )}
      </div>

      <div className="mt-6 flex justify-between">
        <Button data-testid="wiz-back" variant="ghost" disabled={step === 0} onClick={() => setStep(step - 1)} className="text-slate-300"><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>
        {step < STEPS.length - 1 && (
          <Button data-testid="wiz-next" disabled={!canNext()} onClick={() => setStep(step + 1)} className="bg-[#C5A059] hover:bg-[#b18f47] text-[#070C1E] font-semibold">Next <ChevronRight className="w-4 h-4 ml-1" /></Button>
        )}
      </div>
    </div>
  );
}
